import React from 'react';

type CalendarMode = 'day' | 'week' | 'month';

interface CalendarNavigatorProps {
  mode: CalendarMode;
  anchorDate: Date;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export const CalendarNavigator: React.FC<CalendarNavigatorProps> = ({
  mode,
  anchorDate,
  onNavigate,
}) => {
  const formatDateLabel = () => {
    const options: Intl.DateTimeFormatOptions = {};

    if (mode === 'day') {
      // Format: "Monday, November 06"
      return anchorDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      });
    } else if (mode === 'week') {
      // Format: "November 06–13" or "Oct 30 – Nov 06" if month changes
      const weekStart = new Date(anchorDate);
      weekStart.setDate(anchorDate.getDate() - anchorDate.getDay() + 1); // Monday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Sunday

      const startMonth = weekStart.getMonth();
      const endMonth = weekEnd.getMonth();

      if (startMonth === endMonth) {
        return `${weekStart.toLocaleDateString('en-US', { month: 'long' })} ${weekStart.getDate()}–${weekEnd.getDate()}`;
      } else {
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      }
    } else {
      // Format: "November 2025"
      return anchorDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
  };

  return (
    <div className="flex items-center justify-between mb-4">
      <button
        onClick={() => onNavigate('prev')}
        aria-label={`Previous ${mode}`}
        className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <h3 className="text-lg font-semibold text-gray-900">{formatDateLabel()}</h3>

      <button
        onClick={() => onNavigate('next')}
        aria-label={`Next ${mode}`}
        className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};
